import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ScheduledPostTarget } from 'types'
import { ScheduledPostTargetsClient } from 'api/clients/ScheduledPostTargetsClient'
import { scheduledPostTargetsKeys } from './scheduledPostTargetsKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ProviderToName } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/constants'
import { CheckBroken } from 'blaze-ui/icons'

export interface Data extends ScheduledPostTarget {}

export interface Variables {
  scheduled_post_id: number
  target_id: number
}

interface Context {
  previousTargets: ScheduledPostTarget[] | undefined
}

export const deleteScheduledPostTarget = async (variables: Variables, workspaceId: number) => {
  const response = await ScheduledPostTargetsClient.delete({
    workspaceId,
    scheduledPostId: variables.scheduled_post_id,
    targetId: variables.target_id,
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error('Unexpected error response')
  }
  return response.data.scheduled_post_target
}

export const useDeleteScheduledPostTarget = () => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<Data, unknown, Variables, Context>(
    'deleteScheduledPostTarget',
    async (variables) => {
      return deleteScheduledPostTarget(variables, workspaceId)
    },
    {
      onMutate: async (deletedTarget) => {
        await queryClient.cancelQueries(scheduledPostTargetsKeys.list(workspaceId, deletedTarget.scheduled_post_id))

        const previousTargets = queryClient.getQueryData<ScheduledPostTarget[]>(
          scheduledPostTargetsKeys.list(workspaceId, deletedTarget.scheduled_post_id),
        )

        const deletedProvider = previousTargets?.find((target) => target.id === deletedTarget.target_id)?.provider

        if (previousTargets) {
          queryClient.setQueryData(
            scheduledPostTargetsKeys.list(workspaceId, deletedTarget.scheduled_post_id),
            previousTargets.filter((target) => target.id !== deletedTarget.target_id),
          )
        }

        if (deletedProvider) {
          showToastMessage(
            `Posting to ${ProviderToName[deletedProvider]} disabled!`,
            'designRating',
            undefined,
            undefined,
            <CheckBroken />,
            'dark',
          )
        }

        return { previousTargets }
      },

      onError: (err, deletedTarget, context) => {
        if (context?.previousTargets !== undefined) {
          queryClient.setQueryData(
            scheduledPostTargetsKeys.list(workspaceId, deletedTarget.scheduled_post_id),
            context.previousTargets,
          )
        }
      },
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries(scheduledPostTargetsKeys.list(workspaceId, variables.scheduled_post_id))
        queryClient.removeQueries(
          scheduledPostTargetsKeys.detail(workspaceId, variables.scheduled_post_id, variables.target_id),
        )
      },
    },
  )
}
